import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { FadeUp, RevealLineInView, RevealImage } from "@/components/animations/Reveal";

// Bento grid spans
const SPANS = [
    "col-span-2 md:col-span-6 md:row-span-2 aspect-[4/5] md:aspect-auto",
    "md:col-span-3 aspect-square",
    "md:col-span-3 aspect-square",
    "md:col-span-4 aspect-[4/3]",
    "md:col-span-4 aspect-[4/3]",
    "col-span-2 md:col-span-4 aspect-[4/3] md:aspect-[4/3]",
];

export default function Gallery() {
    const { t } = useLanguage();
    return (
        <section id="gallery" className="relative bg-white py-16 md:py-36" data-testid="gallery">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-4 mb-8">
                    <span className="chapter-num text-lg">{t.gallery.chapter}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                    <span className="eyebrow">{t.gallery.eyebrow}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
                    <h2 className="lg:col-span-8 font-display text-ink leading-[0.98] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        <RevealLineInView>{t.gallery.title.split(" ")[0]}</RevealLineInView>{" "}
                        <span className="serif-italic text-gold">
                            <RevealLineInView delay={0.1}>{t.gallery.title.split(" ").slice(1).join(" ")}</RevealLineInView>
                        </span>
                    </h2>
                    <FadeUp delay={0.15} className="lg:col-span-4">
                        <p className="text-ink/70 text-base leading-relaxed">{t.gallery.sub}</p>
                    </FadeUp>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-5">
                    {SITE.images.gallery.map((src, i) => (
                        <motion.div
                            key={src}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className={`group relative overflow-hidden rounded-2xl ${SPANS[i] || "md:col-span-4 aspect-[4/3]"}`}
                            data-testid={`gallery-item-${i}`}
                        >
                            <RevealImage
                                src={src}
                                alt={`Gallery ${i + 1}`}
                                delay={(i % 3) * 0.12}
                                className="w-full h-full"
                            />
                            {/* Hover overlay (sits above the reveal) */}
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10" />
                            <div className="pointer-events-none absolute top-4 right-4 text-[0.65rem] tracking-[0.3em] uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10">
                                0{i + 1}
                            </div>
                            {/* Scale on hover sits on the img inside RevealImage — we add an outer transform too */}
                            <div className="pointer-events-none absolute inset-0 transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]" />
                        </motion.div>
                    ))}
                </div>

                <FadeUp delay={0.1} className="mt-14 flex justify-center">
                    <a
                        href={SITE.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline inline-flex items-center gap-3"
                        data-testid="gallery-instagram"
                    >
                        <Instagram className="w-4 h-4" />
                        {t.gallery.follow}
                    </a>
                </FadeUp>
            </div>
        </section>
    );
}
